/**
 * INR ↔ K-INR conversion for the UI.
 *
 * The backend stores every monetary value in K-INR (thousand rupees), but
 * users naturally type in plain rupees. Every amount input in the UI
 * accepts INR and converts to K-INR via these helpers before sending to
 * the API.
 *
 * The conversion is exact: divide by 1000 and round half-even to 3dp using
 * BigInt arithmetic (no floats), matching the server-side `inrToKinr`
 * (Decimal ROUND_HALF_EVEN at `MONEY_SCALE` = 3) in `src/money/decimal.ts`
 * byte-for-byte — including on ties, where float math used to disagree
 * (e.g. "501.50" INR is exactly 0.5015 K-INR, a tie that must round to
 * "0.502", not the float-drifted "0.501").
 */

/** Parse a user-typed INR string. Returns NaN for invalid input. */
export function parseInr(input: string): number {
  const trimmed = input.trim();
  if (trimmed.length === 0) return NaN;
  // Allow optional leading sign, digits, one decimal point, thousand-commas.
  if (!/^-?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\d+(\.\d+)?$/.test(trimmed)) return NaN;
  const stripped = trimmed.replace(/,/g, '');
  const n = parseFloat(stripped);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Convert a user-typed INR string to a K-INR string suitable for API
 * payloads. Returns `null` if the input is empty or invalid.
 *
 * This value is POSTed as the authoritative stored amount for payments,
 * adjustments, and opening balances, so it is computed exactly: K-INR at
 * 3dp ⇔ (INR rounded half-even to the nearest whole rupee) / 1000, all in
 * BigInt — no float ever touches the digits. `parseInr`'s regex remains the
 * accept/reject decision.
 *
 * Example: `"60000"` → `"60.000"`; `"60.5"` → `"0.060"` (tie → even 60);
 * `"501.50"` → `"0.502"` (tie → even 502).
 */
export function inrToKinrString(input: string): string | null {
  if (!Number.isFinite(parseInr(input))) return null;
  const parsed = parseDecimalUnits(input.trim().replace(/,/g, ''));
  if (!parsed) return null;
  // Round |INR| to whole rupees, half to even: compare twice the dropped
  // fraction against one whole unit; on an exact tie keep the even rupee.
  const pow = 10n ** BigInt(parsed.scale);
  const neg = parsed.units < 0n;
  const abs = neg ? -parsed.units : parsed.units;
  let rupees = abs / pow;
  const twiceRemainder = (abs % pow) * 2n;
  if (twiceRemainder > pow || (twiceRemainder === pow && rupees % 2n === 1n)) {
    rupees += 1n;
  }
  // rupees / 1000 as a fixed 3-decimal string.
  const intPart = (rupees / 1000n).toString();
  const fracPart = (rupees % 1000n).toString().padStart(3, '0');
  return `${neg && rupees > 0n ? '-' : ''}${intPart}.${fracPart}`;
}

/**
 * Format an INR amount as a readable K-INR preview string, e.g. "60.000 K-INR".
 * Returns `null` if input is invalid or empty.
 */
export function inrToKinrPreview(input: string): string | null {
  const k = inrToKinrString(input);
  return k === null ? null : `${k} K-INR`;
}

/**
 * Display a K-INR amount as ₹INR with thousand-grouped digits.
 * Example: `"50"` → `"₹50,000"`; `"60.5"` → `"₹60,500"`; `"24.864"` → `"₹24,864"`.
 * Returns the raw input if parsing fails.
 */
export function kinrToInrDisplay(kinr: string): string {
  const n = parseFloat(kinr);
  if (!Number.isFinite(n)) return kinr;
  const rupees = Math.round(n * 1000);
  const formatted = rupees.toLocaleString('en-IN');
  return `₹${formatted}`;
}

/**
 * Convert a stored K-INR string back to a plain-INR string suitable for the
 * amount INPUT of the payment/adjustment dialogs (the "Void & re-enter"
 * prefill). Exact ×1000 — a pure decimal-point shift, no float math.
 *
 * `"60.5"` → `"60500"`; `"24.864"` → `"24864"`; `"0.061"` → `"61"`.
 * Returns `''` for unparseable input.
 */
export function kinrToInrString(kinr: string): string {
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(kinr.trim());
  if (!m) return '';
  const sign = m[1]!;
  const intPart = m[2]!;
  const fracRaw = m[3] ?? '';
  const frac = fracRaw.length < 3 ? (fracRaw + '000').slice(0, 3) : fracRaw;
  const shifted = (intPart + frac.slice(0, 3)).replace(/^0+(?=\d)/, '');
  const rest = frac.slice(3).replace(/0+$/, '');
  return `${sign}${shifted}${rest ? `.${rest}` : ''}`;
}

/**
 * Exact addition of two decimal strings (K-INR balances/amounts) using
 * BigInt — used for the "balance before → after" preview in the void
 * confirmation, so the preview matches the server's Decimal math
 * byte-for-byte. Returns `null` when either input is unparseable.
 */
export function addKinrStrings(a: string, b: string): string | null {
  const pa = parseDecimalUnits(a);
  const pb = parseDecimalUnits(b);
  if (!pa || !pb) return null;
  const scale = Math.max(pa.scale, pb.scale);
  const sum = rescale(pa, scale) + rescale(pb, scale);
  return formatUnits(sum, scale);
}

/** Negate a decimal string exactly (for subtracting via addKinrStrings). */
export function negateKinrString(v: string): string {
  const t = v.trim();
  if (t.startsWith('-')) return t.slice(1);
  return t === '0' || /^0(\.0+)?$/.test(t) ? t : `-${t}`;
}

function parseDecimalUnits(s: string): { units: bigint; scale: number } | null {
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(s.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const units = BigInt(m[2]! + frac) * (m[1] === '-' ? -1n : 1n);
  return { units, scale: frac.length };
}

function rescale(v: { units: bigint; scale: number }, scale: number): bigint {
  return v.units * 10n ** BigInt(scale - v.scale);
}

function formatUnits(units: bigint, scale: number): string {
  const neg = units < 0n;
  const abs = (neg ? -units : units).toString().padStart(scale + 1, '0');
  const intPart = scale === 0 ? abs : abs.slice(0, -scale);
  let frac = scale === 0 ? '' : abs.slice(-scale).replace(/0+$/, '');
  frac = frac.length > 0 ? `.${frac}` : '';
  const out = `${intPart}${frac}`;
  return neg && out !== '0' ? `-${out}` : out;
}
